import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function ClientProfitabilityPanel() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/insights/client-profitability`, { withCredentials: true });
        setData(res.data);
      } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="text-zinc-400 text-sm py-6">Loading client data…</div>;
  if (!data) return null;

  const ratingColors = {
    A: 'bg-green-900/30 text-green-300 border-green-700',
    B: 'bg-blue-900/30 text-blue-300 border-blue-700',
    C: 'bg-amber-900/30 text-amber-300 border-amber-700',
    D: 'bg-red-900/30 text-red-300 border-red-700',
  };

  return (
    <div className="space-y-4" data-testid="clients-panel">
      <div>
        <h3 className="text-zinc-50 font-heading font-semibold text-sm flex items-center gap-2">
          <Users size={14} className="text-blue-400" /> Client Profitability
        </h3>
        <p className="text-zinc-500 text-xs mt-0.5">{data.total_clients} clients · {fmt(data.total_revenue)} total revenue</p>
      </div>
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/60 text-zinc-500 text-xs">
              <tr>
                <th className="text-left px-4 py-2">Client</th>
                <th className="text-right px-4 py-2">Revenue</th>
                <th className="text-right px-4 py-2">Orders</th>
                <th className="text-right px-4 py-2">Avg Order</th>
                <th className="text-right px-4 py-2">Avg Pay Days</th>
                <th className="text-right px-4 py-2">On-Time %</th>
                <th className="text-right px-4 py-2">Margin %</th>
                <th className="text-center px-4 py-2">Rating</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {data.clients.map(c => (
                <tr key={c.client} className="hover:bg-zinc-800/40" data-testid={`client-row-${c.client}`}>
                  <td className="px-4 py-2 text-zinc-200">{c.client}</td>
                  <td className="px-4 py-2 text-right font-mono text-amber-300">{fmt(c.revenue)}</td>
                  <td className="px-4 py-2 text-right font-mono text-zinc-400">{c.orders}</td>
                  <td className="px-4 py-2 text-right font-mono text-zinc-400">{fmt(c.avg_order_value)}</td>
                  <td className="px-4 py-2 text-right font-mono text-zinc-400">{c.avg_payment_days ?? '—'}</td>
                  <td className="px-4 py-2 text-right font-mono">
                    {c.on_time_pct === null ? <span className="text-zinc-600">—</span> : (
                      <span className={c.on_time_pct >= 90 ? 'text-green-300' : c.on_time_pct >= 70 ? 'text-amber-300' : 'text-red-300'}>
                        {c.on_time_pct}%
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right font-mono">
                    {c.gross_margin_pct === null ? <span className="text-zinc-600">—</span> : (
                      <span className={c.gross_margin_pct >= 30 ? 'text-green-300' : c.gross_margin_pct >= 15 ? 'text-amber-300' : 'text-red-300'}>
                        {c.gross_margin_pct}%
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <span className={`px-2 py-0.5 rounded-lg border text-xs font-bold ${ratingColors[c.rating]}`}>{c.rating}</span>
                  </td>
                </tr>
              ))}
              {data.clients.length === 0 && <tr><td colSpan={8} className="text-center text-zinc-500 py-6 text-xs">No client orders yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
